import { useLayoutEffect } from "react";

/**
 * A SESSION END WAITS FOR THE BOOKING (session revocation, 2026-10-06).
 *
 * When Backend ends a credentialed session (SESSION_REVOKED), IdentityProvider
 * signs the client out. Not while a booking is sending, its outcome is
 * unknown, or its answer is on screen: signing out then would hand the next
 * Confirm or Check a different identity (the commit path re-establishes one),
 * and the idempotency key that names the booking is account-scoped. The
 * existing "a booking may exist" handling wins; the sign-out waits.
 *
 * The screens that know say so here while it is true (useBooking's owner,
 * Book; the verified guest checkout's host). The sign-out runs as soon as
 * nothing holds it: the booking resolved, or the person left the screen
 * (unmounting releases its hold).
 *
 * In memory and per tab, deliberately: a revoked session is this tab's
 * request being refused, and another tab gets its own refusal.
 * No Firebase import: hooks that hold stay testable without it.
 */

let holds = 0;
let waiting: Array<() => void> = [];

function drain(): void {
  if (holds > 0) return;
  const run = waiting;
  waiting = [];
  for (const fn of run) {
    try {
      fn();
    } catch {
      // One waiter's failure must not strand the others.
    }
  }
}

/** Hold session ends while a booking may be in flight or may exist. Returns the release. */
export function holdSessionEnd(): () => void {
  holds += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds -= 1;
    drain();
  };
}

/** Whether a session end must wait right now. */
export function sessionEndHeld(): boolean {
  return holds > 0;
}

/**
 * Run `fn` now if nothing holds session ends, otherwise as soon as nothing
 * does. Returns a cancel.
 */
export function whenSessionEndAllowed(fn: () => void): () => void {
  if (holds === 0) {
    fn();
    return () => undefined;
  }
  waiting.push(fn);
  return () => {
    waiting = waiting.filter((entry) => entry !== fn);
  };
}

/**
 * Hold while `active`; released when it turns false or the component unmounts.
 *
 * A LAYOUT effect on purpose (review P1): useBooking hands its hold over when
 * a send resolves to an outcome, releasing it from a PASSIVE effect. Layout
 * effects of a commit run before its passive effects, so the outcome's hold
 * exists before the send's is released and a queued sign-out cannot slip
 * through the gap.
 */
export function useHoldSessionEnd(active: boolean): void {
  useLayoutEffect(() => {
    if (!active) return;
    return holdSessionEnd();
  }, [active]);
}

/** Test-only. */
export function __resetSessionEndGuardForTests(): void {
  holds = 0;
  waiting = [];
}
